import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, LoaderCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { clearAuthError, loginWithOtp, sendOtp } from '../../redux/slices/authSlice';
import { Button } from '../../components/ui/Button';

const maskMobile = (mobile) => {
  if (!mobile || mobile.length < 4) {
    return mobile;
  }

  return `${mobile.slice(0, 4)} ••• ${mobile.slice(-4)}`;
};

export const OtpForm = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language, formatNumber } = useI18n();
  const { authMode, mobile, otpSent, loading, error, isAuthenticated, otpExpiresInSeconds, otpRequestedAt } = useAppSelector((state) => state.auth);
  const [otpDigits, setOtpDigits] = useState(() => Array(6).fill(''));
  const [remainingSeconds, setRemainingSeconds] = useState(otpExpiresInSeconds || 120);
  const inputRefs = useRef([]);
  const lastSubmittedCodeRef = useRef('');
  const otp = useMemo(() => otpDigits.join(''), [otpDigits]);
  const isRegisterMode = authMode === 'register';
  const canResend = remainingSeconds <= 0;
  const totalSeconds = otpExpiresInSeconds || 120;
  const BackIcon = language === 'fa' ? ChevronRight : ChevronLeft;
  const DirectionArrow = language === 'fa' ? ArrowLeft : ArrowRight;
  const continueState = useMemo(() => ({
    openCreateProject: location.state?.openCreateProject,
    projectDraft: location.state?.projectDraft,
    openPublicTemplateId: location.state?.openPublicTemplateId
  }), [location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(location.state?.redirectTo || '/home', {
        replace: true,
        state: location.state?.redirectState || continueState
      });
      return;
    }

    if (!mobile || !otpSent) {
      navigate('/login');
    }
  }, [continueState, isAuthenticated, location.state, mobile, navigate, otpSent]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (!otpRequestedAt) {
      setRemainingSeconds(totalSeconds);
      return undefined;
    }

    const updateCountdown = () => {
      const elapsedSeconds = Math.floor((Date.now() - otpRequestedAt) / 1000);
      setRemainingSeconds(Math.max(totalSeconds - elapsedSeconds, 0));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);

    return () => window.clearInterval(intervalId);
  }, [otpRequestedAt, totalSeconds]);

  const submitOtp = useCallback(async (otpCode) => {
    if (loading || otpCode.length < 6 || !mobile) {
      return;
    }

    lastSubmittedCodeRef.current = otpCode;
    const result = await dispatch(loginWithOtp({ mobile, otpCode }));
    if (loginWithOtp.fulfilled.match(result)) {
      navigate(location.state?.redirectTo || '/home', {
        replace: true,
        state: location.state?.redirectState || continueState
      });
      return;
    }

    lastSubmittedCodeRef.current = '';
  }, [continueState, dispatch, loading, location.state, mobile, navigate]);

  useEffect(() => {
    if (otp.length === 6 && otp !== lastSubmittedCodeRef.current) {
      submitOtp(otp);
    }
  }, [otp, submitOtp]);

  const onLogin = async (event) => {
    event.preventDefault();
    submitOtp(otp);
  };

  const onResend = async () => {
    if (!mobile || !canResend || loading) {
      return;
    }

    setOtpDigits(Array(6).fill(''));
    lastSubmittedCodeRef.current = '';
    await dispatch(sendOtp(mobile));
    inputRefs.current[0]?.focus();
  };

  const onDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const nextDigits = [...otpDigits];
    nextDigits[index] = digit;
    setOtpDigits(nextDigits);
    dispatch(clearAuthError());

    if (digit && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const onDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const onPaste = (event) => {
    const pastedValue = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pastedValue) {
      return;
    }

    event.preventDefault();
    const nextDigits = Array(6).fill('');
    pastedValue.split('').forEach((digit, index) => {
      nextDigits[index] = digit;
    });
    setOtpDigits(nextDigits);
    dispatch(clearAuthError());
    inputRefs.current[Math.min(pastedValue.length, 6) - 1]?.focus();
  };

  const formattedTime = formatNumber(
    `${String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}:${String(remainingSeconds % 60).padStart(2, '0')}`
  );

  return (
    <>
      <button
        type="button"
        onClick={() => navigate('/login')}
        className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-muted transition hover:text-text"
      >
        <BackIcon size={16} />
        {t('editMobile')}
      </button>

      <header>
        <h1 className="text-2xl font-bold tracking-tight text-text">
          {isRegisterMode ? t('registerVerifyTitle') : t('verifyTitle')}
        </h1>
        <p className="mt-2 text-sm text-muted">
          {t('verifySentTo')}{' '}
          <span className="font-semibold text-text" dir="ltr">
            {formatNumber(maskMobile(mobile))}
          </span>
        </p>
      </header>

      <form className="mt-8 space-y-5" onSubmit={onLogin}>
        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('otpLabel')}</label>
          <div className="grid grid-cols-6 gap-2" dir="ltr">
            {otpDigits.map((digit, index) => (
              <input
                key={index}
                ref={(element) => {
                  inputRefs.current[index] = element;
                }}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={digit}
                onChange={(event) => onDigitChange(index, event.target.value)}
                onKeyDown={(event) => onDigitKeyDown(index, event)}
                onPaste={onPaste}
                className={`aspect-square min-h-11 w-full rounded-xl border bg-panel-strong text-center text-xl font-bold outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 ${
                  digit ? 'border-accent' : 'border-border'
                }`}
                aria-label={`${t('otpLabel')} ${index + 1}`}
              />
            ))}
          </div>
          <p className="text-xs text-muted">{t('verifyInputHint')}</p>
        </div>

        <p className="text-center text-sm text-muted">
          {t('verifyTimerLabel')}{' '}
          <span className="font-semibold tabular-nums text-text" dir="ltr">
            {formattedTime}
          </span>
        </p>

        {error ? (
          <p className="text-sm text-rose-600 dark:text-rose-300" role="alert">
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          className="min-h-12 w-full rounded-xl text-base font-semibold"
          disabled={loading || otp.length < 6}
        >
          {loading ? (
            <>
              <LoaderCircle size={18} className="animate-spin" />
              {t('loginLoading')}
            </>
          ) : (
            <>
              {isRegisterMode ? t('completeRegister') : t('login')}
              <DirectionArrow size={18} />
            </>
          )}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm">
        <button
          type="button"
          onClick={onResend}
          disabled={loading || !canResend}
          className="font-medium text-accent disabled:cursor-not-allowed disabled:text-muted"
        >
          {canResend ? t('resendOtp') : t('resendOtpIn', { time: formattedTime })}
        </button>
      </p>
    </>
  );
};
